import { Card } from "@components/cards/card";
import { InputClickToCopy } from "@components/forms/input-click-to-copy";
import { IRaffleDefaultDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import { getRaffleDrawType } from "@hooks/functions/Raffle/drawType";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import { FiAward, FiShield, FiTag } from "react-icons/fi";
import { FaTicketAlt } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";

interface IProps {
  raffle: IRaffleDefaultDTO;
}

interface ITicketSummary {
  sold: number;
  total: number;
  price: number;
  rewardPriceMarket: number;
}

const masks = new Masks();

export const RaffleSummaryVisitor = ({ raffle }: IProps) => {
  const { actions: { pushToastify } } = ZuToastifyStore();
  const [summary, setSummary] = useState<ITicketSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const status = getRaffleStatus(raffle.status);
  const drawType = getRaffleDrawType(raffle.drawType);
  const progress = summary?.total ? Math.min(100, Math.round(summary.sold / summary.total * 100)) : 0;
  const shareLink = `${window.location.origin}/campaign/default?${new URLSearchParams({ campaign: raffle.campaign, key: raffle.key })}`;

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/raffleClient/default/tickets/summary", { params: { campaign: raffle.campaign, key: raffle.key } })
      .then(({ data }) => { if (active) setSummary(data); })
      .catch((error) => {
        if (active) pushToastify({
          message: error?.response?.data?.message || "Falha ao carregar o resumo dos bilhetes.",
          type: "error",
          theme: "dark",
        });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [raffle.key, raffle.campaign, pushToastify]);

  return (
    <Card className="visit-panel visit-summary">
      <div className="visit-summary-media">
        {raffle.thumb && <img src={raffle.thumb} alt={raffle.name} />}
      </div>
      <div className="visit-summary-content">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <span className="visit-eyebrow"><FiAward /> Rifa</span>
          <span className={`badge bg-${status.color} text-${status.color === "warning" || status.color === "info" ? "dark" : "white"}`} title={status.title}>{status.text}</span>
        </div>
        <h1>{raffle.name}</h1>
        <p className="visit-muted mb-3">{raffle.description_short}</p>
        <div className="visit-summary-metrics">
          <div className="visit-metric"><FaTicketAlt /><span>Valor do bilhete<strong>{masks.brlCurrency(summary?.price ?? raffle.price)}</strong></span></div>
          <div className="visit-metric"><FiAward /><span>Prêmio total<strong>{masks.brlCurrency(summary?.rewardPriceMarket ?? raffle.rewardPriceMarket)}</strong></span></div>
          <div className="visit-metric"><FiTag /><span>Campanha<strong title={raffle.campaign}>#{raffle.campaign}</strong></span></div>
        </div>
        {loading ? <Skeleton className="mt-3" height={32} /> : (
          <div className="visit-progress mt-3">
            <div className="d-flex justify-content-between gap-2 mb-2"><strong>{masks.formatMilhar(summary?.sold || 0)} de {masks.formatMilhar(summary?.total || raffle.quantity)} bilhetes vendidos</strong><strong>{progress}%</strong></div>
            <div className="progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}><div className="progress-bar" style={{ width: `${progress}%` }} /></div>
          </div>
        )}
        {drawType && (
          <div className="visit-draw-note mt-3"><FiShield size={20} /><span><strong>{drawType.title}</strong><small>{drawType.description}</small></span></div>
        )}
        <div className="visit-share mt-3"><InputClickToCopy label="Compartilhar campanha" value={shareLink} /></div>
      </div>
    </Card>
  );
};
